import React, { memo } from "react";

import { BreakpointKeys, breakpoints } from "../config/breakpoints";
import useWindowWidth from "../hooks/useWindowWidth";
import DesktopNavbar, { ClosableNavbar } from "./DesktopNavbar/DesktopNavbar";
import MobileNavbar from "./MobileNavbar/MobileNavbar";
import "./Navbar.scss";
import TopNavbar from "./TopNavbar/TopNavbar";

export type NavItemType = {
  type?: "submenu" | "link";
  href?: string;
  link?: string;
  text: string;
  icon?: React.ReactNode;
  className?: string;
  submenu?: NavItemType[];
  hilight?: boolean;
};

export type NavbarLayout = "sidebar" | "top";

interface NavbarProps {
  navItems: NavItemType[];
  beforeChildren?: React.ReactNode;
  children?: React.ReactNode;
  closeable?: ClosableNavbar;
  mobileBeforeChildren?: React.ReactNode;
  mobileChildren?: React.ReactNode;
  showLogo?: boolean;
  showLinkLogo?: boolean;
  breakpoint?: BreakpointKeys;
  scrollToContainer?: boolean;
  layout?: NavbarLayout;
}

const Navbar: React.FC<NavbarProps> = ({
  navItems,
  children,
  beforeChildren,
  mobileBeforeChildren,
  mobileChildren,
  closeable = {
    closeable: false,
    defaultOpen: true,
    closedNavbarWidth: "90px",
  },
  showLogo = true,
  showLinkLogo = true,
  breakpoint = "breakpoint-lg-4",
  scrollToContainer = false,
  layout = "sidebar",
}) => {
  const { windowWidth } = useWindowWidth();

  const isDesktop = windowWidth > breakpoints[breakpoint];

  if (layout === "top") {
    return (
      <>
        {isDesktop ? (
          <TopNavbar
            navItems={navItems}
            beforeChildren={beforeChildren}
            showLogo={showLogo}
            showLinkLogo={showLinkLogo}
          >
            {children}
          </TopNavbar>
        ) : (
          <MobileNavbar
            navItems={navItems}
            showLinkLogo={showLinkLogo}
            mobileBeforeChildren={mobileBeforeChildren}
            scrollToContainer={scrollToContainer}
          >
            {mobileChildren}
          </MobileNavbar>
        )}
      </>
    );
  }

  return (
    <>
      {isDesktop ? (
        <DesktopNavbar
          navItems={navItems}
          beforeChildren={beforeChildren}
          closeable={closeable}
          showLogo={showLogo}
          showLinkLogo={showLinkLogo}
        >
          {children}
        </DesktopNavbar>
      ) : (
        <MobileNavbar
          navItems={navItems}
          showLinkLogo={showLinkLogo}
          mobileBeforeChildren={mobileBeforeChildren}
          scrollToContainer={scrollToContainer}
        >
          {mobileChildren}
        </MobileNavbar>
      )}
    </>
  );
};

export default memo(Navbar);
